import InfiniteScroll from 'react-infinite-scroll-component';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useRegisterClassListQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import IssuerNotificationItem from '@/modules/issuer-dashboard/components/items/IssuerNotificationItem.tsx';
import { IssuerAddNavModal } from '@/modules/issuer-dashboard/components/nav-managment/IssuerAddNavModal.tsx';
import { SelectDate } from '@/modules/issuer-dashboard/components/transactions/SelectDate.tsx';
import { SelectRegisterClass } from '@/modules/issuer-dashboard/components/transactions/SelectRegisterClass.tsx';
import { SelectWithEnum } from '@/modules/issuer-dashboard/components/transactions/SelectWithEnum.tsx';
import { useIssuerNotificationsListQuery } from '@/modules/issuer-dashboard/queries/event-logs.ts';
import { useIssuerNavListQuery } from '@/modules/issuer-dashboard/queries/nav.tsx';
import { Button, ChartEmptyState, ModalProviderContext, SelectPaginateOption, Spinner } from 'ui';
import { EventLogType } from 'api';

enum EventNavLogType {
  NavCreated = 'nav_created',
  NavUpdated = 'nav_updated',
  NavDeleted = 'nav_deleted',
}
//
export const UpdateLogTab = () => {
  const { openModal } = useContext(ModalProviderContext);
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const [registerClass, setRegisterClass] = useState<SelectPaginateOption<number> | undefined>(
    undefined,
  );
  const [type, setType] = useState<EventNavLogType>();
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const {
    data: dataNotificationsList,
    isFetchingNextPage,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
  } = useIssuerNotificationsListQuery(
    {
      page: 1,
      page_size: 15,
      from_date: dates?.[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : undefined,
      to_date: dates?.[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : undefined,
      register_class_id: registerClass?.value,
      types:
        type ||
        [EventLogType.NavUpdated, EventLogType.NavCreated, EventLogType.NavDeleted].join(','),
    },
    registerId,
  );

  const { data: dataRegisterClassList } = useRegisterClassListQuery({
    page: 1,
    page_size: 1,
    register_id: +registerId,
  });

  const { data: navList } = useIssuerNavListQuery(
    {
      page: 1,
      page_size: 20,
      register_class_id: registerClass?.value ?? 0,
    },
    !registerClass?.value,
  );

  const activeNav = useMemo(
    () => navList?.pages?.flatMap(page => page.data).find(item => item.is_active === true),
    [navList?.pages],
  );

  useEffect(() => {
    const firstClass = dataRegisterClassList?.data?.[0];
    if (!registerClass || firstClass?.id !== registerClass?.value) {
      setRegisterClass(
        firstClass?.id ? { value: firstClass?.id, label: firstClass?.class_name } : undefined,
      );
    }
  }, [dataRegisterClassList]);

  const notifications = useMemo(
    () => (dataNotificationsList?.pages || []).flatMap(({ data }) => data),
    [dataNotificationsList],
  );

  const handleAdd = () => {
    openModal(IssuerAddNavModal, {
      register_class_id: registerClass?.value ?? 0,
      activeDate: new Date(activeNav?.changes_date ?? new Date()),
    });
  };

  const handleChangeType = useCallback(
    (option: EventNavLogType | null) => {
      setType(option || undefined);
    },
    [setType],
  );

  const handleChangeRequestedDate = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      setDates(dates);
    },
    [setDates],
  );

  return (
    <div className="flex flex-col w-full gap-4">
      <div className="flex xl:flex-row flex-col xl:items-center items-start xl:justify-between justify-start px-3 sm:px-6 gap-4">
        <SelectRegisterClass
          registerId={registerId}
          registerClass={registerClass}
          setRegisterClass={setRegisterClass}
        />
        <div className="flex lg:flex-row flex-col xl:items-center items-start gap-4">
          <div>
            <SelectWithEnum<EventNavLogType>
              enumValue={EventNavLogType}
              value={type}
              label="Types"
              isClearable={true}
              handleChange={handleChangeType}
              placeholder="All types"
            />
          </div>
          <div>
            <SelectDate
              handleChangeDates={handleChangeRequestedDate}
              dates={dates}
              label="Date range"
            />
          </div>
          <div>
            <Button className="w-[96px]" onClick={handleAdd}>
              New item
            </Button>
          </div>
        </div>
      </div>
      <div className="px-3 sm:px-6">
        {!notifications?.length && (
          <div className="py-4 flex justify-center items-center font-medium text-lg">
            <ChartEmptyState isError={isError} isLoading={isLoading} />
          </div>
        )}
        {(!isLoading || isFetchingNextPage) && (
          <InfiniteScroll
            next={fetchNextPage}
            dataLength={notifications.length}
            hasMore={!!hasNextPage}
            style={{ overflow: 'unset' }}
            loader={
              <div className="flex justify-center items-center h-16 w-full">
                <Spinner size="md" variant="primary" />
              </div>
            }
            scrollableTarget="scrollable"
          >
            <div className="w-full flex flex-col gap-2">
              {notifications.map(notification => (
                <IssuerNotificationItem key={notification.id} notification={notification} />
              ))}
            </div>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};
